import { makeStyles, TextField } from "@material-ui/core";
import dayjs from "dayjs";
import { FC, useState } from "react";
import { useNotify, useRefresh } from "react-admin";

import { TimeStampClass } from "../../../types/firebase.types";
import { orderReservationDocRef } from "../../../utils/referencies";
import { applyUpdateTimestamp } from "../../../utils/timestamp";
import { OrderDeliveryMethods, OrderStatus } from "../types";
import EditableText from "./EditableText";

const useStyles = makeStyles({
  expectedDeliveryDateStyle: {
    marginRight: "3px",

    "& input": {
      fontSize: "14px",
      padding: "8px 5px",
    },
  },
});

const formatDate = (date: any = dayjs(), format = "DD-MM-YYYY"): string => dayjs(date).format(format);

const ExpectedDeliveryDate: FC<any> = ({ record, source }) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();

  const [latestExpectedDeliveryDate, setLatestExpectedDeliveryDate] = useState<Date>(() =>
    record[source] instanceof TimeStampClass ? record[source].toDate() : record[source]
  );
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<Date>(latestExpectedDeliveryDate);
  const [loading, setLoading] = useState(false);

  const isSameExpectedDeliveryDate = () => formatDate(expectedDeliveryDate) === formatDate(latestExpectedDeliveryDate);
  const isSameOrFutureDate = (date: Date) => dayjs(date).isAfter(dayjs()) || formatDate(date) === formatDate();
  const isStandardDeliveryMethod = () => record.deliveryMethod === OrderDeliveryMethods.standard;
  const isCanceledOrDeliveredOrder = () =>
    record.status === OrderStatus.canceled || record.status === OrderStatus.delivered;

  const handleSelectExpectedDate = (e: any) => {
    e.stopPropagation();
    setExpectedDeliveryDate(new Date(e.target.value));
  };

  const handleUpdate = () => {
    setLoading(true);

    return new Promise<{ updatedValue: any }>((resolve, reject) => {
      return orderReservationDocRef(record.id)
        .update(
          applyUpdateTimestamp({
            expectedDeliveryDate: expectedDeliveryDate,
          })
        )
        .then(() => {
          resolve({ updatedValue: formatDate(expectedDeliveryDate) });
          notify("Expected delivery date has been updated", "success");
          setLoading(false);
          setLatestExpectedDeliveryDate(expectedDeliveryDate);
          refresh();
        })
        .catch((ex) => {
          console.log(ex.message);
          reject({ originalValue: formatDate(latestExpectedDeliveryDate) });
          notify("Error on update expected delivery date", "error");
          setLoading(false);
        });
    });
  };

  return (
    <EditableText
      sourceOriginalValue={formatDate(record[source].toDate())}
      canEdit={
        isSameOrFutureDate(expectedDeliveryDate) &&
        isSameOrFutureDate(latestExpectedDeliveryDate) &&
        isStandardDeliveryMethod() &&
        !isCanceledOrDeliveredOrder()
      }
      onUpdate={handleUpdate}
      message={isSameExpectedDeliveryDate() ? "Same day, change it!" : "Save"}
      isDisabled={isSameExpectedDeliveryDate()}
      colorValue={isSameOrFutureDate(expectedDeliveryDate) ? "textPrimary" : "textSecondary"}
    >
      <TextField
        type="date"
        variant="outlined"
        className={classes.expectedDeliveryDateStyle}
        onChange={handleSelectExpectedDate}
        onClick={(e: any) => e.stopPropagation()}
        defaultValue={formatDate(expectedDeliveryDate, "YYYY-MM-DD")}
        disabled={loading}
        inputProps={{
          min: formatDate(dayjs(), "YYYY-MM-DD"),
          required: true,
        }}
      />
    </EditableText>
  );
};

export default ExpectedDeliveryDate;
